/**
 * ErrorSummary Component
 * Groups multiple validation errors with clear instructions and recovery guidance
 * Ensures calculation errors appear prominently in results area
 */

import React from 'react'
import styles from './ErrorSummary.module.css'

interface ErrorItem {
  fieldId: string
  fieldLabel: string
  message: string
  severity: 'error' | 'warning' | 'info'
  recoveryGuidance?: string
}

interface ErrorSummaryProps {
  errors: ErrorItem[]
  title?: string
  className?: string
  position?: 'top' | 'results-area'
  showRecoveryGuidance?: boolean
  onErrorClick?: (fieldId: string) => void
  hideBox?: boolean // New prop to hide the error summary box
}

const ErrorSummary: React.FC<ErrorSummaryProps> = ({
  errors,
  title = 'Please fix the following issues:',
  className = '',
  position = 'top',
  showRecoveryGuidance = true,
  onErrorClick,
  hideBox = false
}) => {
  if (!errors || errors.length === 0) {
    return null
  }

  // If hideBox is true, don't render the component but still handle the logic
  if (hideBox) {
    return null
  }

  // Group errors by severity
  const groupedErrors = errors.reduce((groups, error) => {
    const severity = error.severity
    if (!groups[severity]) {
      groups[severity] = []
    }
    groups[severity].push(error)
    return groups
  }, {} as Record<string, ErrorItem[]>)

  const handleErrorClick = (fieldId: string) => {
    if (onErrorClick) {
      onErrorClick(fieldId)
    } else {
      // Default behavior: focus the field
      const field = document.getElementById(fieldId)
      if (field) {
        field.focus()
        field.scrollIntoView({ behavior: 'smooth', block: 'center' })
        
        // Announce to screen readers
        const announcement = `Focused on ${fieldId.replace(/([A-Z])/g, ' $1').toLowerCase()} field`
        const liveRegion = document.createElement('div')
        liveRegion.setAttribute('aria-live', 'polite')
        liveRegion.setAttribute('aria-atomic', 'true')
        liveRegion.className = 'sr-only'
        liveRegion.textContent = announcement
        document.body.appendChild(liveRegion)
        
        // Clean up after announcement
        setTimeout(() => {
          document.body.removeChild(liveRegion)
        }, 1000)
      }
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent, fieldId: string) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleErrorClick(fieldId)
    }
  }

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'error':
        return (
          <svg 
            className={styles.severityIcon} 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
            />
          </svg>
        )
      case 'warning':
        return (
          <svg 
            className={styles.severityIcon} 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" 
            />
          </svg>
        )
      case 'info':
        return (
          <svg 
            className={styles.severityIcon} 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
            />
          </svg>
        )
      default:
        return null
    }
  }

  const getSeverityLabel = (severity: string) => {
    switch (severity) {
      case 'error':
        return 'Errors'
      case 'warning':
        return 'Warnings'
      case 'info':
        return 'Information'
      default:
        return severity
    }
  }

  return (
    <div 
      className={`${styles.errorSummary} ${styles[position]} ${className}`}
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
      data-testid="error-summary"
    >
      <div className={styles.header}>
        <div className={styles.titleContainer}>
          <svg 
            className={styles.headerIcon} 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" 
            />
          </svg>
          <h3 className={styles.title}>{title}</h3>
        </div>
        <div className={styles.errorCount}>
          {errors.length} issue{errors.length !== 1 ? 's' : ''}
        </div>
      </div>

      <div className={styles.content}>
        {/* Render errors by severity order: error, warning, info */}
        {['error', 'warning', 'info'].map(severity => {
          const severityErrors = groupedErrors[severity]
          if (!severityErrors || severityErrors.length === 0) {
            return null
          }

          return (
            <div key={severity} className={styles.severityGroup}>
              <div className={styles.severityHeader}>
                {getSeverityIcon(severity)}
                <h4 className={styles.severityTitle}>
                  {getSeverityLabel(severity)} ({severityErrors.length})
                </h4>
              </div>

              <ul className={styles.errorList} role="list">
                {severityErrors.map((error, index) => (
                  <li 
                    key={`${error.fieldId}-${index}`} 
                    className={`${styles.errorItem} ${styles[severity]}`}
                    role="listitem"
                  >
                    <div className={styles.errorContent}>
                      <button
                        type="button"
                        className={styles.errorButton}
                        onClick={() => handleErrorClick(error.fieldId)}
                        onKeyDown={(e) => handleKeyDown(e, error.fieldId)}
                        aria-describedby={`error-${error.fieldId}-${index}`}
                        aria-label={`Fix ${error.fieldLabel}: ${error.message}`}
                      >
                        <strong className={styles.fieldLabel}>
                          {error.fieldLabel}:
                        </strong>
                        <span className={styles.errorMessage}>
                          {error.message}
                        </span>
                      </button>

                      {showRecoveryGuidance && error.recoveryGuidance && (
                        <div 
                          className={styles.recoveryGuidance}
                          id={`error-${error.fieldId}-${index}`}
                        >
                          <svg 
                            className={styles.guidanceIcon} 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                          >
                            <path 
                              strokeLinecap="round" 
                              strokeLinejoin="round" 
                              strokeWidth={2} 
                              d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" 
                            />
                          </svg>
                          <span>{error.recoveryGuidance}</span>
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )
        })}
      </div>

      {/* General recovery guidance */}
      {showRecoveryGuidance && (
        <div className={styles.generalGuidance}>
          <div className={styles.guidanceHeader}>
            <svg 
              className={styles.guidanceIcon} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
              />
            </svg>
            <span className={styles.guidanceTitle}>How to fix these issues:</span>
          </div>
          <ul className={styles.guidanceList}>
            <li>Click on any error above to jump directly to that field</li>
            <li>Fix the highlighted issues and the errors will disappear automatically</li>
            <li>Once all errors are resolved, you can calculate your ROI</li>
          </ul>
        </div>
      )}

      {/* Screen reader announcement */}
      <div className={styles.srOnly} aria-live="polite" aria-atomic="true">
        {errors.length} validation {errors.length === 1 ? 'error' : 'errors'} found. 
        Please review and correct the issues listed above.
      </div>
    </div>
  )
}

export default ErrorSummary